/**
 * CostByAgentChart — shared admin component.
 *
 * Mounted under:
 *   - /admin/analytics?tab=llm-costs (Command Center)
 *
 * Renders three stacked sections off /api/admin/plog/cost-by-agent:
 *   1. Per-provider rollup tile strip (total$, calls, %, avg $/call)
 *   2. Stacked horizontal bar chart (one bar per prompt_id, segments
 *      colored by provider, model-level breakdown in the tooltip)
 *   3. Flat table sorted by total $ desc with a provider pill column
 *
 * Was previously co-located inside LLMMonitor.jsx; extracted so it can
 * be reused in the Analytics Command Center without dragging the rest
 * of LLM Monitor with it.
 */
import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

// Provider color palette — kept in sync with the dashboard pill colors
// in LLM Monitor's "LLM Call Logs" panel.
const PROVIDER_COLORS = {
  anthropic: "#F97316", // orange
  openai: "#10B981",    // emerald
  gemini: "#3B82F6",    // blue
  google: "#3B82F6",    // alias — some old rows wrote "google" instead of "gemini"
  deepseek: "#06B6D4",  // cyan
  groq: "#A855F7",      // purple
  unknown: "#6B7280",   // gray (catches null/missing provider rows)
};
const providerColor = (p) => PROVIDER_COLORS[p] || PROVIDER_COLORS.unknown;

// ─── Custom stacked-bar tooltip (module-scope to keep React reconciler
// happy — defining it inside the component would unmount on every render).
const CostByAgentTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const breakdown = row?._breakdown || [];
  const totalCalls = breakdown.reduce((acc, b) => acc + (b.call_count || 0), 0);
  const blendedAvg = totalCalls > 0 ? row._total / totalCalls : 0;
  return (
    <div className="bg-divine-black/95 border border-divine-gold/25 rounded-lg p-2 text-xs">
      <div className="text-divine-gold font-mono mb-1.5">{label}</div>
      <div className="text-gray-400 mb-1.5">
        Total ${row._total.toFixed(4)}
        <span className="text-gray-500"> · </span>
        <span className="text-emerald-300 font-mono">${blendedAvg.toFixed(5)}/call</span>
      </div>
      {breakdown
        .slice()
        .sort((a, b) => b.total_cost - a.total_cost)
        .map((b, i) => (
          <div key={i} className="flex items-center gap-1.5 leading-tight">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: providerColor(b.provider) }}
            />
            <span className="text-gray-300">{b.model || "—"}</span>
            <span className="text-gray-500">·</span>
            <span className="text-divine-gold font-mono">${b.total_cost.toFixed(4)}</span>
            <span className="text-gray-500">({b.call_count.toLocaleString()})</span>
            <span className="text-gray-500">·</span>
            <span className="text-emerald-300 font-mono">${(b.avg_cost ?? 0).toFixed(5)}/call</span>
          </div>
        ))}
    </div>
  );
};

// Stable Recharts <Legend> formatter — module scope to avoid
// react/no-unstable-nested-components warnings.
const legendFormatter = (value) => (
  <span style={{ color: "rgba(255,255,255,0.75)" }}>{value}</span>
);

export default function CostByAgentChart() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await authAxios.get(`/admin/plog/cost-by-agent?days=${days}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        console.log("cost-by-agent load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [days]);

  const agents = data?.agents || [];
  const providers = data?.providers || [];

  // ─── Stacked-bar pivot ───────────────────────────────────────────────
  // Pivot the flat (prompt_id, provider, model) rows into one row per
  // prompt_id, keyed by provider. Each provider key holds the $ that the
  // agent spent on that provider in the window — Recharts stacks them.
  // We carry a hidden `_breakdown` array so the tooltip can show
  // model-level detail (provider→model→$→calls→$/call) on hover.
  const byPrompt = new Map();
  for (const row of agents) {
    const key = row.prompt_id;
    let entry = byPrompt.get(key);
    if (!entry) {
      entry = { name: key, _total: 0, _breakdown: [] };
      byPrompt.set(key, entry);
    }
    const prov = row.provider || "unknown";
    entry[prov] = (entry[prov] || 0) + row.total_cost;
    entry._total += row.total_cost;
    entry._breakdown.push({
      provider: prov,
      model: row.model,
      total_cost: row.total_cost,
      call_count: row.call_count,
      avg_cost: row.avg_cost_per_call,
    });
  }
  const chartData = Array.from(byPrompt.values()).sort((a, b) => b._total - a._total);

  // Discover which providers actually appear in the window so we render
  // exactly that many <Bar> stacks (Recharts requires one <Bar> per key).
  const activeProviders = Array.from(
    new Set(agents.map(a => a.provider || "unknown"))
  ).sort((a, b) => {
    const aTotal = providers.find(p => p.provider === a)?.total_cost || 0;
    const bTotal = providers.find(p => p.provider === b)?.total_cost || 0;
    return bTotal - aTotal;
  });

  return (
    <div className="border-t border-divine-gold/20 pt-8" data-testid="cost-by-agent-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-divine-gold flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Cost by Agent
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Per-agent spend over the last {days} days, stacked by LLM provider (estimated, via LiteLLM)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32 bg-white/5 border-white/10" data-testid="cost-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hrs</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Per-provider rollup strip — at-a-glance "which LLM is costing
          what" view, purpose-built for migration decisions.
          Now includes Avg $/Call so cross-provider comparison is direct. */}
      {data && providers.length > 0 && (
        <div
          className="grid gap-2 mb-3"
          style={{ gridTemplateColumns: `repeat(${Math.min(providers.length, 5)}, minmax(0, 1fr))` }}
          data-testid="cost-provider-strip"
        >
          {providers.map((p) => {
            const pct = data.grand_total_cost > 0
              ? (p.total_cost / data.grand_total_cost) * 100
              : 0;
            const avgPerCall = p.call_count > 0 ? p.total_cost / p.call_count : 0;
            return (
              <div
                key={p.provider}
                className="rounded-lg p-3 border"
                style={{
                  borderColor: `${providerColor(p.provider)}40`,
                  background: `${providerColor(p.provider)}12`,
                }}
                data-testid={`cost-provider-tile-${p.provider}`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: providerColor(p.provider) }}
                  />
                  <p
                    className="text-[10px] uppercase tracking-wide font-semibold"
                    style={{ color: providerColor(p.provider) }}
                  >
                    {p.provider}
                  </p>
                </div>
                <p className="text-xl font-mono text-white mt-1">${p.total_cost.toFixed(2)}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {p.call_count.toLocaleString()} calls · {pct.toFixed(1)}%
                </p>
                <p className="text-[10px] mt-0.5">
                  <span className="text-gray-500">avg </span>
                  <span className="text-emerald-300 font-mono">${avgPerCall.toFixed(5)}</span>
                  <span className="text-gray-500">/call</span>
                </p>
              </div>
            );
          })}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-lg p-3" data-testid="cost-total">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total Spend</p>
            <p className="text-2xl font-mono text-divine-gold mt-1">
              ${data.grand_total_cost.toFixed(2)}
            </p>
          </div>
          <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-lg p-3">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total Calls</p>
            <p className="text-2xl font-mono text-white mt-1">
              {data.grand_total_calls.toLocaleString()}
            </p>
          </div>
          <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-lg p-3">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Avg $/Call (Window)</p>
            <p className="text-2xl font-mono text-emerald-300 mt-1">
              ${data.grand_total_calls > 0
                ? (data.grand_total_cost / data.grand_total_calls).toFixed(4)
                : "0.0000"}
            </p>
          </div>
        </div>
      )}

      <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-xl p-4">
        {loading && <p className="text-sm text-gray-400 text-center py-12">Loading…</p>}
        {!loading && chartData.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-12">
            No traffic in the selected window.
          </p>
        )}
        {!loading && chartData.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 36)}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                  tickFormatter={(v) => `$${v.toFixed(2)}`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={180}
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }}
                />
                <RTooltip
                  content={<CostByAgentTooltip />}
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                  iconType="circle"
                  formatter={legendFormatter}
                />
                {activeProviders.map((p, idx) => (
                  <Bar
                    key={p}
                    dataKey={p}
                    name={p}
                    stackId="cost"
                    fill={providerColor(p)}
                    radius={
                      idx === activeProviders.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="overflow-x-auto mt-4 border-t border-divine-gold/10 pt-3">
              <table className="w-full text-xs" data-testid="cost-by-agent-table">
                <thead>
                  <tr className="text-gray-400">
                    <th className="px-2 py-1 text-left font-medium">prompt_id</th>
                    <th className="px-2 py-1 text-left font-medium">provider</th>
                    <th className="px-2 py-1 text-left font-medium">model</th>
                    <th className="px-2 py-1 text-right font-medium">calls</th>
                    <th className="px-2 py-1 text-right font-medium">total $</th>
                    <th className="px-2 py-1 text-right font-medium">$/call</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divine-gold/5">
                  {agents.map((a, i) => (
                    <tr key={`${a.prompt_id}-${a.provider}-${a.model}-${i}`} className="text-gray-200">
                      <td className="px-2 py-1.5 font-mono">{a.prompt_id}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{
                            background: `${providerColor(a.provider)}20`,
                            color: providerColor(a.provider),
                          }}
                        >
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{ background: providerColor(a.provider) }}
                          />
                          {a.provider || "unknown"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-400 font-mono">{a.model || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{a.call_count.toLocaleString()}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-divine-gold">${a.total_cost.toFixed(4)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-300">${a.avg_cost_per_call.toFixed(5)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
